import { EntityKind, GroundOrigin, SIXTEENTHS, VISION_CAP_TILES, lootHook, lootRank, type EntityState, type Rules } from "odyssey-shared";
import { groundItemOf } from "./find.ts";

/**
 * Which landing a page sounds (P2.8-T77, the author, 2026-10-10: "We will have a sound for high tier items when they
 * drop too."): of the ground items a drawing saw land (`GroundLandings.note`), those the server marked the world's
 * (`GroundOrigin.World`: "No player induced drop will play the sound."), of a tier with a sound (Rare and up), inside
 * the vision cap around the character's feet; the highest tier of them plays once, at the first item of that tier, so
 * a kill's pile sounds once. The cap keeps out an old drop the view only now holds: the server's margin sends an item
 * before the character could see it, so one inside the cap that the page had not noted has just landed. Pure, and it
 * allocates nothing but the one answer.
 */

/**
 * Half the vision cap in sixteenths on each axis, made once.
 */
const CAP_HALF_X = (VISION_CAP_TILES.width / 2) * SIXTEENTHS;
const CAP_HALF_Y = (VISION_CAP_TILES.height / 2) * SIXTEENTHS;

/**
 * The landing sound to play: its hook and where the item lies from the feet, in tiles, right and down positive.
 */
export interface LandingSound {
  readonly hook: string;
  readonly dx: number;
  readonly dy: number;
}

/**
 * The one landing sound a drawing plays, if any.
 *
 * @param landed The handles seen landing in the drawing, the first `count` of them read.
 * @param count How many of `landed` are this drawing's.
 * @param entities The client's view.
 * @param rules The page's rules, which name each item's rarity.
 * @param feet The character's feet in sixteenths, undefined while none is drawn.
 * @returns The sound, or null when nothing plays.
 * @example
 * loudestLanding(view.landed, view.landedCount, entities, PAGE_RULES, predicted);
 * // { hook: "sfx/world/loot/epic", dx: 1.5, dy: -0.25 }
 */
export function loudestLanding(landed: readonly number[], count: number, entities: ReadonlyMap<number, EntityState>, rules: Rules, feet: { readonly x: number; readonly y: number } | undefined): LandingSound | null {
  if (feet === undefined) {
    return null;
  }
  let best: EntityState | null = null;
  let bestRank = -1;
  let bestHook: string | null = null;
  for (let index = 0; index < count; index += 1) {
    const state = entities.get(landed[index] as number);
    if (state === undefined || state.kind !== EntityKind.GroundItem || state.level !== GroundOrigin.World) {
      continue;
    }
    if (Math.abs(state.x - feet.x) > CAP_HALF_X || Math.abs(state.y - feet.y) > CAP_HALF_Y) {
      continue;
    }
    const item = groundItemOf(rules, state.appearance);
    const hook = item === null ? null : lootHook(item.rarity);
    if (item === null || hook === null) {
      continue;
    }
    const rank = lootRank(item.rarity);
    if (rank > bestRank) {
      best = state;
      bestRank = rank;
      bestHook = hook;
    }
  }
  return best === null || bestHook === null ? null : { hook: bestHook, dx: (best.x - feet.x) / SIXTEENTHS, dy: (best.y - feet.y) / SIXTEENTHS };
}

/**
 * Whether the character's feet jumped between two snapshots: further than half the vision cap on either axis, which no
 * step, run or dash covers, as a respawn or a teleport within the session does (P2.8-T77). The page then starts its
 * landings' settling again, so never-noted items around the new place do not all hop and sound.
 *
 * @param from The feet at the last snapshot in sixteenths, undefined when none was drawn.
 * @param to The feet now, undefined when none is drawn.
 * @returns True for a jump.
 */
export function feetJumped(from: { readonly x: number; readonly y: number } | undefined, to: { readonly x: number; readonly y: number } | undefined): boolean {
  return from !== undefined && to !== undefined && (Math.abs(to.x - from.x) > CAP_HALF_X || Math.abs(to.y - from.y) > CAP_HALF_Y);
}
